import dayjs from '@/services/date'
import formatDate from '@/utils/format-date'
import { DayOfWeek } from '@/shared/constants/date/DayOfWeek'
import TimeDenominations from '@/shared/constants/date/TimeDenominations'
import DateFormatToken from '@/shared/constants/date/DateFormatToken'
import {
  AusDateFormats,
  USADateFormats,
} from '@/shared/constants/date/DateFormats'
import { DurationUnits } from '@/shared/constants/date/DurationUnits'
import hasPassedTimeToday from '@/utils/has-passed-time-today'
import locationMixin from './locationMixin'

const { SATURDAY, SUNDAY, MONDAY } = DayOfWeek

const {
  MILLISECONDS_PER_SECOND,
  MILLISECONDS_PER_MINUTE,
  MILLISECONDS_PER_HOUR,
  MILLISECONDS_PER_DAY,
  MILLISECONDS_PER_WEEK,
} = TimeDenominations

export default {
  mixins: [locationMixin],
  data: () => ({
    usaShortDateFormat: DateFormatToken.MMDDYYWithSlash,
    ausShortDateFormat: DateFormatToken.DDMMYYWithSlash,
    durationUnits: DurationUnits,
    dateFormatToken: DateFormatToken,
    timeFormats: [
      DateFormatToken.Hmm,
      DateFormatToken.HHmm,
      DateFormatToken.HHmmss,
    ],
  }),
  computed: {
    dateFormats() {
      return this.isUSALoginOrLocation ? USADateFormats : AusDateFormats
    },
    firstDayOfWeek() {
      return this.isUSALoginOrLocation ? SUNDAY : MONDAY
    },
    localShortDateFormat() {
      return (
        this.dateFormats.find((format) =>
          [this.ausShortDateFormat, this.usaShortDateFormat].includes(format)
        ) || this.ausShortDateFormat
      )
    },
  },
  methods: {
    formatDate,
    /**
     * Returns a new date object
     * @returns {Date}
     */
    getDate(date) {
      return dayjs(date).toDate()
    },
    /**
     * Returns a date object that represents today's date
     * @returns {Date}
     */
    getTodaysDate() {
      return this.getDate()
    },
    hasPassedTimeToday,

    /**
     * Checks if a date falls between a start and end date/time
     *
     * ```js
     * this.isBetween('09:00', '17:00', '13:00', 'HHmm') // true
     * this.isBetween('2021-01-01', '2021-01-31', '2021-01-15') // true
     * ```
     *
     * @param {string|Date} spanStart - Start of the range
     * @param {string|Date} spanEnd - End of the range
     * @param {string|Date} date - Date to check
     * @param {string} [token] - Optional format token for time strings (e.g. 'HHmm')
     * @returns {boolean}
     */
    isBetween(spanStart, spanEnd, date, unit, formats) {
      const start = this.normalizeToDayjs(spanStart, formats)
      const end = this.normalizeToDayjs(spanEnd, formats)
      const check = this.normalizeToDayjs(date, formats)

      if (!start || !end || !check) return false
      return check.isBetween(start, end, unit, '[]')
    },
    /**
     * This indicates whether the date is before the other supplied date-time.
     *
     * ```js
     * this.isBefore('2011-01-01', '2011-01-02') // default milliseconds
     * ```
     *
     * If you want to limit the granularity to a unit other than milliseconds,
     * pass it as the second parameter.
     *
     * In that case, the comparison respects the given unit and the units above.
     *
     * ```js
     * this.isBefore('2011-01-01', '2011-01-02', DurationUnits.MONTH) // compares month and year
     * ```
     *
     * @param {*} dateA
     * @param {*} dateB
     * @param {string?} unit - See {@link DurationUnits} for full list of units
     * @returns {boolean}
     */
    isBefore(a, b, unit, formats) {
      const dateA = this.normalizeToDayjs(a, formats)
      const dateB = this.normalizeToDayjs(b, formats)
      if (!dateA || !dateB) return false
      return dateA.isBefore(dateB, unit)
    },
    /**
     * Indicates if a date is same or before another date.
     *
     * ```js
     * this.isSameOrBefore('2011-01-01', '2011-01-02', DurationUnits.MONTH) // compares month and year
     * ```
     *
     * @param {*} dateA
     * @param {*} dateB
     * @param {string?} unit - See {@link DurationUnits} for full list of units
     * @returns {boolean}
     */
    isSameOrBefore(dateA, dateB, unit) {
      const dateAWithDate = this.normalizeToDayjs(dateA, unit)
      const dateBWithDate = this.normalizeToDayjs(dateB, unit)
      if (!dateAWithDate || !dateBWithDate) return false

      return dateAWithDate.isSameOrBefore(dateBWithDate, unit)
    },
    /**
     * Indicates if a date is same or after another date.
     *
     * ```js
     * this.isSameOrAfter('2011-01-01', '2011-01-02', DurationUnits.MONTH) // compares month and year
     * ```
     *
     * @param {*} dateA
     * @param {*} dateB
     * @param {string?} unit - See {@link DurationUnits} for full list of units
     * @returns {boolean}
     */
    isSameOrAfter(dateA, dateB, unit) {
      const dateAWithDate = this.normalizeToDayjs(dateA, unit)
      const dateBWithDate = this.normalizeToDayjs(dateB, unit)
      if (!dateAWithDate || !dateBWithDate) return false

      return dayjs(dateAWithDate).isSameOrAfter(dayjs(dateBWithDate), unit)
    },
    /**
     * This indicates whether the date object is after the other supplied date-time.
     *
     * ```js
     * this.isAfter('2011-01-02', '2011-01-01') // default milliseconds
     * ```
     *
     * If you want to limit the granularity to a unit other than milliseconds,
     * pass it as the second parameter. In that case the comparision respects
     * the given unit and the units above.
     *
     * ```js
     * this.isAfter('2011-01-02', '2011-01-01', DurationUnits.MONTH) // compares month and year
     * ```
     *
     * @param {*} dateA
     * @param {*} dateB
     * @param {string?} unit - See {@link DurationUnits} for full list of units
     * @returns {boolean}
     */
    isAfter(a, b, unit, formats) {
      const dateA = this.normalizeToDayjs(a, formats)
      const dateB = this.normalizeToDayjs(b, formats)

      if (!dateA || !dateB) return false
      return dateA.isAfter(dateB, unit)
    },
    /**
     * This indicates whether the date is the same as the other supplied date-time.
     *
     * Can be used to compare dateTime - dateTime OR time - time strings.
     *
     * eg. this.isSame('2025-06-30T00:00:00', '2025-06-30T00:00:00') // true
     *     this.isSame('09:00:00', '09:00:00') // true
     *     this.isSame('2025-06-30T09:00:00', '09:00:00') // false - format dateTime to time string first
     *
     * ```js
     * this.isSame('2011-01-01', '2011-01-01') // default milliseconds
     * ```
     * If you want to limit the granularity to a unit other than milliseconds,
     * pass it as the third parameter.
     *
     * When including a third parameter, it will match all units equal or larger.
     * Passing in month will check month and year.
     * Passing in day will check day, month, and year.
     *
     * ```js
     * this.isSame('2011-01-01', '2011-01-01', DurationUnits.YEAR) // true
     * ```
     *
     * @param {*} dateA
     * @param {*} dateB
     * @param {string?} unit - See {@link DurationUnits} for full list of units
     * @returns
     */
    isSame(a, b, unit, formats) {
      const dateA = this.normalizeToDayjs(a, formats)
      const dateB = this.normalizeToDayjs(b, formats)
      if (!dateA || !dateB) return false
      return dateA.isSame(dateB, unit)
    },
    /**
     * Returns a date object and sets it to the start of a unit of time.
     *
     * ```js
     * this.startOf('2025-04-01', DurationUnits.YEAR)
     * ```
     *
     * @param {*} date
     * @param {string} unit - See {@link DurationUnits} for full list of units
     * @returns {Date}
     */
    startOf(date, unit) {
      return dayjs(date)
        .startOf(unit)
        .toDate()
    },
    /**
     * This indicates the difference between two date-time in the specified unit.
     *
     * The difference is defaulted to milliseconds if no unit is provided
     *
     * ```js
     * this.diff('2019-01-25', '2018-06-05') // 20214000000 default milliseconds
     * ```
     *
     * To get the difference in another unit of measurement, pass that
     * measurement as the second argument.
     *
     * ```js
     * this.diff('2019-01-25', '2018-06-05', DurationUnits.MONTH) // 7
     * ```
     *
     * @param {*} dateA
     * @param {*} dateB
     * @param {string} unit - See {@link DurationUnits} for full list of units
     * @returns
     */
    diff(dateA, dateB, unit) {
      const dateAWithDate = this.normalizeToDayjs(dateA)
      const dateBWithDate = this.normalizeToDayjs(dateB)
      if (!dateAWithDate || !dateBWithDate) return false
      return dateAWithDate.diff(dateBWithDate, unit)
    },
    /**
     * Generates a duration object that is used in other functions.
     *
     * For example, the humanReadableDuration function
     *
     * Usage:
     * ```js
     * this.duration(this.diff('2019-01-25', '2018-06-05'))
     * ```
     *
     * @param {*} difference
     * @param {string?} unit - See {@link DurationUnits} for full list of units
     * @returns
     */
    duration(difference, unit) {
      return dayjs.duration(difference, unit)
    },
    /**
     * Returns a human readable duration based on the difference between
     * two dates.
     *
     * ```js
     * this.humanReadableDuration('2019-01-25', '2019-01-26') // a day
     * ```
     *
     * Setting the 3rd parameter will set how the difference is compared. Defaults to {@link DurationUnits.DAY}.
     * See `diff()` for more details.
     *
     * Setting the 4th parameter to true will add a suffix to the string
     *
     * ```js
     * this.humanReadableDuration('2019-01-25T10:30:00', '2019-01-25T10:29:00', DurationUnits.MINUTE, true) // a minute ago
     * ```
     *
     * @param {*} dateA
     * @param {*} dateB
     * @param {string?} unit - See {@link DurationUnits} for full list of units (Default: {@link DurationUnits.DAY})
     * @param {boolean?} displaySuffix adds suffix to duration string (e.g. in a minute)
     * @returns {string}
     */
    humanReadableDuration(
      dateA,
      dateB,
      unit = DurationUnits.DAY,
      displaySuffix = false
    ) {
      const diff = this.diff(dateA, dateB, unit)
      if (diff === false) return ''

      return this.duration(diff, unit).humanize(displaySuffix)
    },
    /**
     * Returns a new date object with a specified amount of time added
     *
     * ```js
     * this.dateAdd('2019-01-25', 1, DurationUnits.DAY) // date object that represents 2019-01-26
     * ```
     *
     * @param {*} date
     * @param {number} amount
     * @param {string} unit - See {@link DurationUnits} for full list of units
     * @returns {Date}
     */
    dateAdd(date, amount, unit) {
      return dayjs(date)
        .add(amount, unit)
        .toDate()
    },
    /**
     * Returns a new date object with a specified amount of time subtracted
     *
     * ```js
     * this.dateSubtract('2019-01-25', 1, DurationUnits.DAY) // date object that represents 2019-01-24
     * ```
     *
     * @param {*} date
     * @param {number} amount
     * @param {string} unit - See {@link DurationUnits} for full list of units
     * @returns {Date}
     */
    dateSubtract(date, amount, unit) {
      return dayjs(date)
        .subtract(amount, unit)
        .toDate()
    },
    formatDateToFriendlyString(rawDate) {
      const date = typeof rawDate === 'string' ? new Date(rawDate) : rawDate
      const day = date.getDate()
      const month = date.toLocaleString('en-US', { month: 'long' })
      const year = date.getFullYear()

      return `${this.getOrdinalSuffix(day)} ${month} ${year}`
    },
    formatTimesToFriendlyDuration(startDate, endDate) {
      const timeFormat = DateFormatToken.Hmm

      const startTime = this.formatDate(startDate, timeFormat)
      const endTime = this.formatDate(endDate, timeFormat)

      return `${startTime} - ${endTime}`
    },
    convertDateToTimezone(date, tz) {
      return dayjs(date).tz(tz)
    },
    isValidDate(dateStr) {
      if (typeof dateStr !== 'string') return false

      return dayjs(dateStr, this.dateFormats, true).isValid()
    },
    isValidTime(timeStr) {
      if (typeof timeStr !== 'string' || timeStr.length < 4) return false

      return dayjs(timeStr, this.timeFormats, true).isValid()
    },
    dateIsWeekday(date) {
      const parsedDate = typeof date === 'string' ? new Date(date) : date
      return ![SATURDAY, SUNDAY].includes(parsedDate.getDay())
    },
    adjustDateToNextWeekday(date) {
      const EXTRA_DAYS_FOR_WEEKEND = {
        [SATURDAY]: 2,
        [SUNDAY]: 1,
      }
      const extraDays = EXTRA_DAYS_FOR_WEEKEND[date.getDay()] || 0
      date.setDate(date.getDate() + extraDays)
      return date
    },
    getDateSpanDuration(startDateStr, endDateStr, durationType) {
      if (!startDateStr || !endDateStr) return null

      const startDate = new Date(startDateStr)
      const endDate = new Date(endDateStr)

      // Calc absolute diff in ms
      const diffTime = Math.abs(endDate - startDate)

      switch (durationType) {
        case 'seconds':
          return Math.ceil(diffTime / MILLISECONDS_PER_SECOND)
        case 'minutes':
          return Math.ceil(diffTime / MILLISECONDS_PER_MINUTE)
        case 'hours':
          return Math.ceil(diffTime / MILLISECONDS_PER_HOUR)
        case 'days':
          return Math.floor(diffTime / MILLISECONDS_PER_DAY)
        case 'weeks':
          return Math.floor(diffTime / MILLISECONDS_PER_WEEK)
        default:
          return Math.floor(diffTime / MILLISECONDS_PER_DAY)
      }
    },
    getOrdinalSuffix(day) {
      // Range where the "th" suffix always applies (4 through 20)
      const LOWER_BOUND_TH = 4
      const UPPER_BOUND_TH = 20

      if (day >= LOWER_BOUND_TH && day <= UPPER_BOUND_TH) {
        return `${day}th`
      }

      // Constants for switch cases (last digit of the day)
      const FIRST_DIGIT = 1
      const SECOND_DIGIT = 2
      const THIRD_DIGIT = 3

      switch (day % 10) {
        case FIRST_DIGIT:
          return `${day}st`
        case SECOND_DIGIT:
          return `${day}nd`
        case THIRD_DIGIT:
          return `${day}rd`
        default:
          return `${day}th`
      }
    },
    dateIsWeekend(date) {
      const { SATURDAY, SUNDAY } = DayOfWeek
      const parsedDate = typeof date === 'string' ? new Date(date) : date

      return [SATURDAY, SUNDAY].includes(dayjs(parsedDate).day())
    },
    /**
     * Groups dates into consecutive ranges and formats them using the specified format
     * @param {Date[]} dates Array of dates to group
     * @param {string} format Format to use for the date strings (from DateFormatToken)
     * @param {boolean} ignoreWeekends Whether to treat weekends as part of the same range
     * @returns {string[]} Array of formatted date ranges
     */
    formatDateRanges(
      dates,
      format = DateFormatToken.DoMMM,
      ignoreWeekends = false
    ) {
      if (!dates?.length) return []

      const sortedDates = [...dates].sort((a, b) => a - b)
      const ranges = []
      let currentRange = []

      const isGapAllowed = (prev, curr) => {
        const daysBetween = this.getDateSpanDuration(prev, curr, 'days')
        if (daysBetween <= 1) return true
        if (!ignoreWeekends) return false

        const datesInBetween = Array.from({ length: daysBetween - 1 }, (_, i) =>
          this.dateAdd(prev, i + 1, DurationUnits.DAY)
        )
        return datesInBetween.every(this.dateIsWeekend)
      }

      sortedDates.forEach((date, i) => {
        const prevDate = sortedDates[i - 1]
        const isNewRange = i === 0 || !isGapAllowed(prevDate, date)
        const shouldStartNewRange = isNewRange && currentRange.length > 0

        if (shouldStartNewRange) {
          ranges.push(currentRange)
          currentRange = [date]
        } else if (isNewRange) {
          currentRange = [date]
        } else {
          currentRange.push(date)
        }
      })

      if (currentRange.length) ranges.push(currentRange)

      return ranges.map((range) => {
        const firstDate = range[0]
        const lastDate = range[range.length - 1]
        const formattedStart = this.formatDate(firstDate, format)
        const formattedEnd = this.formatDate(lastDate, format)

        return range.length === 1
          ? formattedStart
          : `${formattedStart} - ${formattedEnd}`
      })
    },
    normalizeToDayjs(input, formats = [], referenceDate = null) {
      if (dayjs.isDayjs(input)) return input
      if (input instanceof Date) return dayjs(input)
      if (typeof input !== 'string') return null

      const isTime = /^(\d{1,2}):(\d{2})(?::(\d{2}))?$/.test(input)

      const refDate = referenceDate || dayjs().format('YYYY-MM-DD')

      const normalized = isTime ? dayjs(`${refDate} ${input}`) : dayjs(input)

      return normalized.isValid() ? normalized : null
    },
  },
}
